import { BaseApi } from '@shared/config';
import type {
  GetUsersRequest,
  UpdateUserRequest,
  User,
  CreateUserRequest,
  DeleteUserRequest,
  ChangeActivityRequest,
  ChangeActivityResponse,
  ListUserAiMessagesRequest,
  ListUserAiMessagesResponse,
} from './types';

// Backend waits for the AI reply before responding.
const SEND_USER_AI_MESSAGE_TIMEOUT_MS = 30_000;

class UserApi extends BaseApi {
  getUser = (id: string): Promise<User> => {
    return this.instance.get(`users/${id}`);
  };

  getUsers = <R>(params: GetUsersRequest): Promise<R> => {
    return this.instance.get(`users`, { params });
  };

  updateUser = (id: string, data: UpdateUserRequest): Promise<User> => {
    return this.instance.put(`users/${id}`, data);
  };

  createUser = (data: CreateUserRequest): Promise<User> => {
    return this.instance.post(`users`, data);
  };

  deleteUser = (data: DeleteUserRequest): Promise<{}> => {
    return this.instance.delete(`users`, { data });
  };

  changeActivity = (
    id: string,
    data: ChangeActivityRequest
  ): Promise<ChangeActivityResponse> => {
    return this.instance.put(`users/${id}/change-activity`, data);
  };

  listUserAiMessages = (
    id: string,
    params: ListUserAiMessagesRequest,
    signal?: AbortSignal
  ): Promise<ListUserAiMessagesResponse> => {
    return this.instance.get(`users/${id}/messages`, { params, signal });
  };

  sendUserAiMessage = (
    id: string,
    data: string | File,
    type: 'text' | 'audio'
  ) => {
    const messageType = {
      text: 'messageText',
      audio: 'messageFile',
    };

    if (type === 'text') {
      return this.instance.post(
        `users/${id}/message`,
        {
          [messageType[type]]: data,
        },
        {
          timeout: SEND_USER_AI_MESSAGE_TIMEOUT_MS,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    }

    if (type === 'audio' && data instanceof File) {
      const formData = new FormData();
      formData.append(messageType[type], data);

      return this.instance.post(`users/${id}/message`, formData, {
        timeout: SEND_USER_AI_MESSAGE_TIMEOUT_MS,
        headers: {},
        transformRequest: data => data,
      });
    }

    throw new Error(`Invalid data type for ${type} message`);
  };

  exportUsers = ({
    dateFrom,
    dateTo,
  }: {
    dateFrom: string | null;
    dateTo?: string | null;
  }) => {
    return this.instance.post(
      `users/export`,
      {
        data: dateFrom &&
          dateTo && {
            dateFrom,
            dateTo,
          },
      },
      {
        responseType: 'blob',
      }
    );
  };
}

export const userApi = new UserApi();
